// Centralized notification manager
// Handles Web Notification API, sounds, vibration, badge count

import { playNotificationSound, vibrateDevice } from './notificationSounds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let notificationSettings = {
  notifications_enabled: true,
  message_sound: true,
  message_vibrate: true,
  show_preview: true,
  group_notifications: true,
  ringtone: 'default',
  muted_chats: []
};

// Initialize from localStorage on module load
try {
  const saved = localStorage.getItem('notificationSettings');
  if (saved) {
    notificationSettings = { ...notificationSettings, ...JSON.parse(saved) };
  }
} catch {}

// Request notification permission on app load
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  const result = await Notification.requestPermission();
  return result;
};

// Load user's notification settings
export const loadNotificationSettings = (settings) => {
  if (settings) {
    notificationSettings = { ...notificationSettings, ...settings };
    try {
      localStorage.setItem('notificationSettings', JSON.stringify(notificationSettings));
    } catch {}
  }
};

// Update badge count
export const updateBadgeCount = (count) => {
  if ('setAppBadge' in navigator) {
    if (count > 0) {
      navigator.setAppBadge(count).catch(() => {});
    } else {
      navigator.clearAppBadge().catch(() => {});
    }
  }
};

// Show notification for new message
export const showMessageNotification = ({ senderName, content, chatId, chatType, messageType, isCurrentChat = false }) => {
  // Don't notify if user is currently viewing this chat
  if (isCurrentChat) return;

  // Always re-read from localStorage to get the freshest settings
  try {
    const saved = localStorage.getItem('notificationSettings');
    if (saved) {
      notificationSettings = { ...notificationSettings, ...JSON.parse(saved) };
    }
  } catch {}

  // Check if notifications are enabled
  if (!notificationSettings.notifications_enabled) return;

  // Check if this chat is muted
  if (notificationSettings.muted_chats?.includes(chatId)) return;

  // Check group notification setting
  if (chatType === 'group' && !notificationSettings.group_notifications) return;

  // Play sound only if enabled
  if (notificationSettings.message_sound) {
    playNotificationSound(notificationSettings.ringtone || 'default');
  }

  // Vibrate only if enabled
  if (notificationSettings.message_vibrate) {
    vibrateDevice([100, 50, 100]);
  }

  // Show browser notification (heads-up / banner)
  if (Notification.permission === 'granted') {
    // Default body when preview is disabled
    let body = 'New message';
    if (notificationSettings.show_preview) {
      body = content || 'New message';
      if (messageType === 'image') body = 'Photo';
      if (messageType === 'video') body = 'Video';
      if (messageType === 'voice' || messageType === 'audio') body = 'Voice message';
      if (messageType === 'file') body = 'Document';
    }

    const notifOptions = {
      body,
      icon: `${BACKEND_URL}/favicon.ico`,
      badge: `${BACKEND_URL}/favicon.ico`,
      tag: `chat-${chatId}`,
      renotify: true,
      requireInteraction: false,
      silent: !notificationSettings.message_sound,
      data: { chatId, url: `/chat/${chatId}` }
    };

    // Always use the basic Notification constructor - more reliable click handling on mobile
    const notification = new Notification(senderName || 'ChatBuzzz', notifOptions);
    notification.onclick = (event) => {
      event.preventDefault();
      notification.close();
      // Store pending navigation
      localStorage.setItem('pending_nav_chat', chatId);
      // Dispatch custom event so App.js can navigate immediately (no waiting for focus)
      window.dispatchEvent(new CustomEvent('notification-nav', { detail: { chatId } }));
      // Focus the window
      window.focus();
    };
    setTimeout(() => notification.close(), 8000);
  }
};
